import { useState } from 'react';
import { Shield, Flame, Phone, Mail, MapPin, CheckCircle, AlertTriangle, Building, Zap, FileCheck, Siren, Droplets, DoorOpen, Wind, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contacts`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    {
      icon: <Siren className="w-12 h-12" />,
      title: 'Fire Detection Systems',
      description: 'Installation, testing and maintenance of AS 1670.1 compliant fire detection and alarm systems including smoke detectors, heat sensors, and manual call points for commercial and residential properties.',
      image: 'https://customer-assets.emergentagent.com/job_fire-safety-3/artifacts/a0iunumo_Fire%20Panel.jpg'
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: 'Emergency Lighting',
      description: 'AS 2293 compliant emergency and exit lighting installation, testing, and 6-monthly maintenance to ensure safe evacuation pathways meet Australian building code requirements.',
      image: 'https://customer-assets.emergentagent.com/job_safefire/artifacts/d2gjj7ta_EEL.jpg'
    },
    {
      icon: <Flame className="w-12 h-12" />,
      title: 'Fire Extinguishers',
      description: 'Supply, installation, and AS 1851 compliant 6-monthly servicing of portable fire extinguishers including ABE dry chemical, CO2, foam, and wet chemical units across all building classifications.',
      image: 'https://customer-assets.emergentagent.com/job_safefire/artifacts/qqwxwn5e_Extinguishers.jpg'
    },
    {
      icon: <Phone className="w-12 h-12" />,
      title: 'Emergency Callouts',
      description: '24/7 emergency response service for urgent fire safety system repairs, alarm faults, and critical equipment failures. Rapid attendance to maintain compliance and building safety.',
      image: 'https://customer-assets.emergentagent.com/job_safefire/artifacts/gfytsqyw_Callout.jpg'
    },
    {
      icon: <FileCheck className="w-12 h-12" />,
      title: 'Essential Safety Measure Reports',
      description: 'Annual ESM reporting as required by Building Act 1993 and Building Regulations 2018. Comprehensive testing, inspection, and documentation of all fire safety equipment to maintain occupancy permits.',
      image: 'https://customer-assets.emergentagent.com/job_safefire/artifacts/pu114ben_AESMR.jpg'
    },
    {
      icon: <Droplets className="w-12 h-12" />,
      title: 'Fire Sprinklers',
      description: 'Design, installation, testing, and AS 1851 maintenance of automatic fire sprinkler systems including hydrant boosters, fire pumps, and sprinkler flow testing for commercial premises.',
      image: 'https://customer-assets.emergentagent.com/job_fire-safety-3/artifacts/70xooyz2_Sprinkler.jpg'
    },
    {
      icon: <DoorOpen className="w-12 h-12" />,
      title: 'Fire Doors',
      description: 'Installation, maintenance, and annual inspection of fire and smoke doors including hardware, seals, and self-closers. Compliance certification to AS 1905.1 and BCA requirements.',
      image: 'https://customer-assets.emergentagent.com/job_safefire/artifacts/2hbmdrd7_fire%20door.jpg'
    },
    {
      icon: <Wind className="w-12 h-12" />,
      title: 'Fire Hose Reels',
      description: 'Installation, hydrostatic pressure testing, and 6-monthly maintenance of fire hose reels to AS 2441 standards. Complete servicing including hose replacement and flow testing.',
      image: 'https://customer-assets.emergentagent.com/job_fire-safety-3/artifacts/n2neokx9_hose%20reel.jpg'
    },
    {
      icon: <Droplets className="w-12 h-12" />,
      title: 'Fire Hydrants',
      description: 'Installation, annual flow testing, and maintenance of fire hydrant systems to AS 2419.1 standards. Internal and external hydrant servicing including valve inspection and pressure testing.',
      image: 'https://customer-assets.emergentagent.com/job_fire-safety-3/artifacts/999v7ho0_Hydrant.jpg'
    }
  ];

  return (
    <div className="landing-page">
      <Toaster position="top-right" richColors />
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_safefire/artifacts/vcg3q2eg_path1000.png" 
              alt="Mertek Logo" 
              className="h-14 w-auto"
            />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-gray-300 hover:text-white font-medium">Services</a>
            <a href="#about" className="text-gray-300 hover:text-white font-medium">About</a>
            <a href="#contact" className="text-gray-300 hover:text-white font-medium">Contact</a>
          </div>
          <Button 
            data-testid="nav-contact-btn"
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white border-0"
          >
            Get In Touch
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_fire-safety-3/artifacts/1ze362ca_background.jpg" 
            alt="Fire Protection"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/70 to-black"></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-950/30 backdrop-blur-sm border border-orange-500/30 rounded-full px-6 py-2 mb-8">
            <CheckCircle className="w-5 h-5 text-orange-400" />
            <span className="text-orange-300 text-sm font-medium">Certified Fire Protection Experts</span>
          </div>
          
          <h1 className="text-7xl md:text-8xl lg:text-9xl font-bold mb-6 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <span className="text-white">Protecting What</span>
            <br />
            <span className="bg-gradient-to-r from-orange-500 via-red-500 to-red-600 bg-clip-text text-transparent">
              Matters Most
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Professional fire protection systems and services to safeguard your business, property, and people.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              data-testid="hero-quote-btn"
              size="lg" 
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white text-lg px-8 py-6 border-0"
            >
              Get In Touch
            </Button>
            <Button 
              data-testid="hero-services-btn"
              size="lg" 
              variant="outline"
              onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              className="bg-white/5 backdrop-blur-sm border-white/20 text-white hover:bg-white/10 text-lg px-8 py-6"
            >
              Our Services
            </Button>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-1/4 left-10 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-10 w-40 h-40 bg-red-500/10 rounded-full blur-3xl"></div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Our <span className="text-orange-500">Services</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Comprehensive fire protection solutions tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card 
                key={index} 
                data-testid={`service-card-${index}`}
                className="group bg-white/5 backdrop-blur-sm border-white/10 hover:border-orange-500/50 hover:shadow-2xl hover:shadow-orange-500/20 overflow-hidden"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110"
                    style={{ 
                      transition: 'transform 0.5s ease',
                      objectPosition: service.title === 'Fire Hydrants' ? 'center top' : 'center center'
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-orange-500">
                    {service.icon}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-3 text-white">{service.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6 relative bg-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-5xl font-bold mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Why Choose <span className="text-orange-500">Mertek?</span>
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                With years of experience in fire protection, Mertek provides industry-leading solutions to keep your property and people safe. Our certified technicians ensure compliance with all safety regulations.
              </p>
              
              <div className="space-y-4">
                {[
                  'Certified and Licensed Professionals',
                  '24/7 Emergency Response',
                  'Comprehensive Inspection Services',
                  'Custom Fire Protection Solutions'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-orange-500 flex-shrink-0" />
                    <span className="text-gray-200 text-lg">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_fire-safety-3/artifacts/27a6g3ee_why%20choose%20mertek.jpeg" 
                alt="Fire Safety"
                className="rounded-2xl shadow-2xl shadow-orange-500/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 relative">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Get In <span className="text-orange-500">Touch</span>
            </h2>
            <p className="text-xl text-gray-400">
              Ready to protect your property? Contact us today for a free consultation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-8">
                <h3 className="text-2xl font-bold mb-6 text-white">Contact Information</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-orange-500/20 p-3 rounded-lg">
                      <Phone className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Phone</p>
                      <p className="text-white text-lg font-medium">1300 117 177</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-orange-500/20 p-3 rounded-lg">
                      <Mail className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Email</p>
                      <p className="text-white text-lg font-medium">admin@mertek.com.au</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-orange-500/20 p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Address</p>
                      <p className="text-white text-lg font-medium">Pakenham, Victoria 3810</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-orange-500/20 p-3 rounded-lg">
                      <Clock className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Business Hours</p>
                      <p className="text-white text-lg font-medium">24/7 Emergency Service</p>
                      <p className="text-orange-500 text-sm font-semibold mt-1">Available Anytime</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-8">
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <div>
                  <Input
                    data-testid="contact-name-input"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-orange-500"
                  />
                </div>
                
                <div>
                  <Input
                    data-testid="contact-email-input"
                    type="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-orange-500"
                  />
                </div>
                
                <div>
                  <Input
                    data-testid="contact-phone-input"
                    type="tel"
                    placeholder="Your Phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-orange-500"
                  />
                </div>
                
                <div>
                  <Textarea
                    data-testid="contact-message-input"
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={5}
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-orange-500 resize-none"
                  />
                </div>
                
                <Button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white py-6 text-lg border-0"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 border-t border-white/10 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_safefire/artifacts/vcg3q2eg_path1000.png" 
                alt="Mertek Logo" 
                className="h-10 w-auto mb-4"
              />
              <p className="text-gray-400 text-sm">
                Professional fire protection services across Victoria
              </p>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">Contact Us</h3>
              <div className="space-y-3">
                <a href="tel:1300117177" className="flex items-center gap-2 text-gray-400 hover:text-orange-500 transition-colors">
                  <Phone className="w-4 h-4" />
                  1300 117 177
                </a>
                <a href="mailto:admin@mertek.com.au" className="flex items-center gap-2 text-gray-400 hover:text-orange-500 transition-colors">
                  <Mail className="w-4 h-4" />
                  admin@mertek.com.au
                </a>
                <div className="flex items-center gap-2 text-gray-400">
                  <MapPin className="w-4 h-4" />
                  Pakenham, Victoria 3810
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">Service Information</h3>
              <div className="space-y-2 text-gray-400 text-sm">
                <p className="text-orange-500 font-semibold text-lg">24/7 Emergency Service</p>
                <p className="mt-3">We're available round the clock for all your fire protection needs.</p>
                <p className="mt-2">Call us anytime: <span className="text-white font-semibold">1300 117 177</span></p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-6 flex items-center justify-between">
            <p className="text-gray-400 text-sm">© 2025 Mertek. All rights reserved.</p>
            <button
              data-testid="admin-login-link"
              onClick={() => window.location.href = '/admin/login'}
              className="text-gray-600 hover:text-gray-400 text-xs transition-colors"
              title="Admin Login"
            >
              Admin
            </button>
          </div>
        </div>
      </footer>

      {/* Emergency Contact Sticky Button */}
      <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50">
        <a
          href="tel:1300117177"
          className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-4 py-3 md:px-6 md:py-4 rounded-full shadow-2xl inline-flex items-center gap-2 md:gap-3 transition-all hover:scale-105"
          title="Call Emergency Line"
        >
          <Phone className="w-5 h-5 animate-pulse flex-shrink-0" />
          <div>
            <div className="text-xs font-semibold opacity-90 whitespace-nowrap leading-tight">EMERGENCY</div>
            <div className="text-sm font-bold whitespace-nowrap leading-tight">1300 117 177</div>
          </div>
        </a>
      </div>

    </div>
  );
};

export default LandingPage;